import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useSyncExternalStore } from 'react';

import { Button } from '@/components/cargofl-ui';
import { EmptyState } from '@/components/empty-state';
import { ServerArt } from '@/components/illustrations';
import { api, apiReachability } from '@/lib/api';

/**
 * Covers the app while the API is unreachable (network failure, 502, 504 — see
 * `apiReachability`), polls /health, and lifts itself once it answers. Like OfflineOverlay it
 * sits OVER the app, so a half-written form survives a deploy restarting the API. Recovery
 * refetches every query rather than reloading, for the same reason. OfflineOverlay renders
 * after this one, so when the browser itself is offline that message wins.
 */
export function ServerDownOverlay() {
  const reachable = useSyncExternalStore(apiReachability.subscribe, apiReachability.get);
  const queryClient = useQueryClient();

  // Any answer from api.get flips `reachable` back by itself.
  const check = useCallback(() => {
    api.get('/health').catch(() => {});
  }, []);

  useEffect(() => {
    if (reachable) return;
    const id = setInterval(check, 5000);
    window.addEventListener('online', check);
    return () => {
      clearInterval(id);
      window.removeEventListener('online', check);
      // The refetch is what replaces the errors the outage left on screen. It runs here, on the
      // way back to reachable, rather than after /health: any request can be the one that lifts
      // the overlay, and then a session query that failed during the outage was never re-asked.
      void queryClient.invalidateQueries();
    };
  }, [reachable, check, queryClient]);

  if (reachable) return null;

  return (
    <div
      role="alertdialog"
      aria-modal="true"
      aria-label="Can't reach the server"
      className="fixed inset-0 z-[100] flex items-center justify-center bg-background animate-in fade-in delay-500 fill-mode-backwards"
    >
      <EmptyState
        art={ServerArt}
        title="Can't reach the server"
        body="CargoFL Performance isn't responding — it may be restarting. This page reconnects on its own, and anything you haven't saved is still here."
      >
        <Button variant="outline" onClick={check}>
          Try again
        </Button>
      </EmptyState>
    </div>
  );
}
